import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="py-16 px-6 md:px-12 bg-light-gray">
        <div className="container max-w-content mx-auto">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0, 0.4, 1] }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Welcome to RANDM</h1>
            <p className="text-xl text-dark-gray mb-8 max-w-2xl">
              A dual-portfolio of diverse and experimental projects by Roscoe and Matthew.
              From travel adventures to music production, comedy writing to podcast episodes.
              There's no theme — just random awesome stuff.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/journeys" className="px-8 py-3 bg-black text-white font-medium hover:bg-opacity-90 transition-colors">
                Explore Projects
              </Link>
              <Link to="/contact" className="px-8 py-3 border border-black font-medium hover:bg-black hover:text-white transition-colors">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* About Section */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="container max-w-content mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="order-2 md:order-1"
            >
              <h2 className="text-3xl font-bold mb-6">The Creators</h2>
              <p className="text-lg text-dark-gray mb-6">
                Creative explorer with a passion for music, technology, and adventure. 
                Constantly seeking new experiences and pushing the boundaries of what's possible.
                Believes in the power of random inspiration to fuel creativity.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="order-1 md:order-2 mb-8 md:mb-0"
            >
              <div className="aspect-square max-w-sm mx-auto md:mx-0 relative overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/roscoe.jpg" 
                  alt="Roscoe" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-12 md:mt-24">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="md:order-2"
            >
              <h2 className="text-3xl font-bold mb-6">Matthew</h2>
              <p className="text-lg text-dark-gray mb-6">
                Versatile creative with an eye for detail and a mind for innovation.
                Balances analytical thinking with artistic expression.
                Thrives in the unexpected intersection of different disciplines and ideas.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="md:order-1"
            >
              <div className="aspect-square max-w-sm mx-auto md:mx-0 relative overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/matthew.jpg" 
                  alt="Matthew" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Why RANDM Section */}
      <section className="py-16 px-6 md:px-12 bg-light-gray">
        <div className="container max-w-content mx-auto text-center">
          <motion.div 
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6">Why RANDM?</h2>
            <p className="text-lg text-dark-gray mb-8">
              RANDM is where our diverse interests collide. It's a space where we showcase projects that don't fit into neat categories.
              Each creation is unique in its own way, but all share our commitment to quality and originality.
            </p>
            <Link to="/journeys" className="inline-block px-8 py-3 bg-black text-white font-medium hover:bg-opacity-90 transition-colors">
              Explore Projects
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Categories Section */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="container max-w-content mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">Explore Categories</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Journeys', path: '/journeys', image: 'https://via.placeholder.com/600x400?text=Journeys', color: '#4A90E2' },
              { name: 'Music', path: '/music', image: 'https://via.placeholder.com/600x400?text=Music', color: '#D0021B' },
              { name: 'Comedy', path: '/comedy', image: 'https://via.placeholder.com/600x400?text=Comedy', color: '#F5A623' },
              { name: 'Podcast', path: '/podcast', image: 'https://via.placeholder.com/600x400?text=Podcast', color: '#7ED321' },
            ].map((category, index) => (
              <motion.div
                key={category.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="mb-6"
              >
                <Link to={category.path} className="block relative aspect-square overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src={category.image} 
                    alt={category.name} 
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: `${category.color}99` }}>
                    <h3 className="text-2xl text-white font-bold">RANDM {category.name}</h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 